import clsx from 'clsx'
import { BATTERY_TONE_BACKGROUNDS, getBatteryTone } from './BatteryLimitBar'

// The backend keeps charge minimums at or below 20% and limits at or above 50%
const MIN_CEILING_PERCENT = 20
const MAX_FLOOR_PERCENT = 50
const THUMB_SIZE_PX = 28

function clampPercent(value: number) {
  return Math.min(Math.max(value, 0), 100)
}

type Props = {
  // null when the car hasn't reported a battery level
  batteryPercent: number | null
  minPercent: number
  maxPercent: number
  step?: number
  onMinChange?: (percent: number) => void
  onMaxChange?: (percent: number) => void
}

/**
 * The Limits page bar, drawn like the dashboard's battery bar: one track with
 * the battery filled in, a thumb for the charge minimum and one for the
 * charge limit.
 */
export default function ChargeLimitsBar({
  batteryPercent: rawBatteryPercent,
  minPercent: rawMinPercent,
  maxPercent: rawMaxPercent,
  step = 1,
  onMinChange,
  onMaxChange,
}: Props) {
  const batteryPercent =
    rawBatteryPercent === null
      ? null
      : clampPercent(Math.round(rawBatteryPercent))
  const minPercent = clampPercent(Math.round(rawMinPercent))
  const maxPercent = clampPercent(Math.round(rawMaxPercent))
  // Each slider owns the half of the track nearest its own thumb, so the two
  // hit areas never fight over a drag
  const splitPercent = (minPercent + maxPercent) / 2

  const thumbStyle = (percent: number) => ({
    left: `clamp(0px, calc(${percent}% - ${
      THUMB_SIZE_PX / 2
    }px), calc(100% - ${THUMB_SIZE_PX}px))`,
  })

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="relative w-full h-7">
        <div className="absolute inset-x-0 h-3 rounded-md top-2 bg-themed-grey-200" />
        <div
          className={clsx(
            'absolute left-0 h-3 rounded-md top-2 transition-[width] duration-500',
            BATTERY_TONE_BACKGROUNDS[getBatteryTone(batteryPercent)],
          )}
          style={{ width: `${batteryPercent ?? 0}%` }}
        />
        <div
          aria-hidden
          className="absolute top-0 box-border bg-white border-2 border-solid rounded-full pointer-events-none w-7 h-7 border-themed-blue-500"
          style={thumbStyle(minPercent)}
        />
        <div
          aria-hidden
          className="absolute top-0 box-border bg-white border-2 border-solid rounded-full pointer-events-none w-7 h-7 border-themed-grey-900"
          style={thumbStyle(maxPercent)}
        />
        <input
          id="charge-limits-minimum"
          type="range"
          min={0}
          max={MIN_CEILING_PERCENT}
          step={step}
          value={minPercent}
          aria-label="Charge minimum"
          aria-valuetext={`Charge minimum ${minPercent}%`}
          onChange={(event) => onMinChange?.(Number(event.target.value))}
          className="absolute top-0 left-0 h-full m-0 opacity-0 cursor-pointer"
          style={{ width: `${splitPercent}%` }}
        />
        <input
          id="charge-limits-maximum"
          type="range"
          min={MAX_FLOOR_PERCENT}
          max={100}
          step={step}
          value={maxPercent}
          aria-label="Charge limit"
          aria-valuetext={`Charge limit ${maxPercent}%`}
          onChange={(event) => onMaxChange?.(Number(event.target.value))}
          className="absolute top-0 right-0 h-full m-0 opacity-0 cursor-pointer"
          style={{ width: `${100 - splitPercent}%` }}
        />
      </div>
      <div className="flex flex-col w-full gap-2 text-xs font-medium leading-5 text-themed-grey-500">
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={clsx(
              'w-3 h-3 rounded-full',
              BATTERY_TONE_BACKGROUNDS[getBatteryTone(batteryPercent)],
            )}
          />
          {batteryPercent === null
            ? 'Battery level unknown'
            : `Battery: ${batteryPercent}%`}
        </span>
        {/* Rings, so the legend matches the two handles on the bar */}
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="box-border w-3 h-3 bg-white border-2 border-solid rounded-full border-themed-blue-500"
          />
          Charge Min: {minPercent}%
        </span>
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="box-border w-3 h-3 bg-white border-2 border-solid rounded-full border-themed-grey-900"
          />
          Charge Limit: {maxPercent}%
        </span>
      </div>
    </div>
  )
}
